/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, Globe, Mail } from 'lucide-react';

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <main className="max-w-4xl mx-auto px-4 py-16 md:py-24" data-testid="privacy-policy-page">
      <div className="mb-10 border-b pb-6">
        <div className="inline-flex items-center gap-2 mb-3 text-[#1565C0] text-xs font-bold uppercase tracking-widest">
          <Shield className="w-4 h-4" />
          Legal · Compliance
        </div>
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-[#0A1628] mb-3">Privacy Policy</h1>
        <p className="text-sm text-gray-500">
          Effective date: 1 January 2026 · EWRF Malaysia (Reg. PPM-001-14-02011979)
        </p>
      </div>

      <article className="prose prose-slate max-w-none space-y-8">
        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628] flex items-center gap-2"><Lock className="w-5 h-5 text-[#D4AF37]" />1. Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            The Educational, Welfare and Research Foundation Malaysia ("EWRF", "we", "us")
            is committed to protecting the personal data of donors, volunteers, beneficiaries,
            programme participants, and every visitor to this website. This Privacy Policy sets
            out how we collect, store, use and disclose personal data in compliance with the
            <strong> Personal Data Protection Act 2010 (Act 709) of Malaysia ("PDPA")</strong>,
            relevant provisions of the <strong>Communications and Multimedia Act 1998</strong>,
            the <strong>Computer Crimes Act 1997</strong>, and — for our international donors —
            the <strong>EU General Data Protection Regulation (GDPR)</strong>, the
            <strong> UK Data Protection Act 2018</strong>, and equivalent standards under
            <strong> Singapore's PDPA 2012</strong>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">2. Data We Collect</h2>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li><strong>Identity data</strong>: name, IC/passport (for donation receipts under Section 44(6)), age, gender.</li>
            <li><strong>Contact data</strong>: address, email, phone, WhatsApp number.</li>
            <li><strong>Financial data</strong>: donation amount, payment method reference (no full card numbers are stored — payments handled by Stripe/DuitNow/iPay88).</li>
            <li><strong>Volunteer & programme data</strong>: qualifications, availability, health information (only when relevant to programme participation).</li>
            <li><strong>Technical data</strong>: IP address, browser type, device type, referring URL, pages visited, cookies (see §7).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">3. Lawful Basis & Purpose</h2>
          <p className="text-gray-700">We process personal data on one or more of these lawful bases:</p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li><strong>Consent</strong> — freely given, specific, informed, unambiguous (e.g. newsletter sign-up).</li>
            <li><strong>Contract</strong> — to fulfil donation receipts, volunteer agreements, event registration.</li>
            <li><strong>Legal obligation</strong> — LHDN tax-exemption reporting under Section 44(6), Registrar of Societies filings.</li>
            <li><strong>Legitimate interest</strong> — impact reporting, fraud prevention, cybersecurity monitoring.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">4. Your Rights (PDPA & GDPR)</h2>
          <p className="text-gray-700">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate or incomplete data</li>
            <li>Withdraw consent at any time</li>
            <li>Object to direct marketing communications</li>
            <li>Request deletion (subject to retention obligations under LHDN / ROS regulations)</li>
            <li>Data portability (GDPR / UK DPA donors)</li>
            <li>Lodge a complaint with Malaysia's Department of Personal Data Protection (JPDP) or your local supervisory authority</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">5. Data Sharing & International Transfers</h2>
          <p className="text-gray-700">
            We do not sell your data. We share only what's necessary with:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Payment processors (Stripe Inc. / iPay88 Sdn Bhd / DuitNow) — for donation processing.</li>
            <li>Cloud infrastructure providers (bounded by data processing agreements with equivalent safeguards).</li>
            <li>Regulatory bodies where required by Malaysian law (LHDN, ROS, PDRM cybercrime unit).</li>
            <li>Programme partners — only for joint programmes and only with prior consent.</li>
          </ul>
          <p className="text-gray-700 mt-2">
            International transfers to non-adequate jurisdictions occur only with Standard
            Contractual Clauses (SCCs) or your explicit consent, per PDPA §129 and GDPR Chapter V.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628] flex items-center gap-2"><Globe className="w-5 h-5 text-[#D4AF37]" />6. Cybersecurity & Data Retention</h2>
          <p className="text-gray-700">
            We employ industry-standard safeguards — TLS 1.3 encryption in transit, encrypted-at-rest
            databases, principle of least privilege, and periodic access reviews — in line with
            <strong> ISO/IEC 27001:2022</strong> guidance and Malaysia's <strong>Cyber Security Act 2024</strong>.
            Personal data is retained only as long as necessary for the purpose collected or as required
            by law (donation records: 7 years per LHDN; volunteer records: 5 years post-service).
            Any confirmed data breach affecting personal data will be notified to affected individuals and
            the relevant authority within <strong>72 hours</strong>, in line with GDPR Article 33 and PDPA
            best practice.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628]">7. Cookies & Analytics</h2>
          <p className="text-gray-700">
            We use essential cookies to run the site (session, CSRF tokens) and privacy-friendly
            analytics to measure traffic. We do not use cross-site advertising trackers. You may block
            cookies in your browser settings — the donation flow may not function without essential
            cookies.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-serif font-bold text-[#0A1628] flex items-center gap-2"><Mail className="w-5 h-5 text-[#D4AF37]" />8. Contact & Complaints</h2>
          <p className="text-gray-700">
            Send data access, correction, or deletion requests to
            <a href="mailto:admin@ewrf.org.my" className="text-[#1565C0] hover:underline"> admin@ewrf.org.my</a>
            {' '}or by post to: EWRF Malaysia, 4B, Persiaran Zaaba, Taman Tun Dr Ismail, 60000 Kuala Lumpur.
            We will respond within <strong>21 days</strong>. Unresolved complaints may be escalated to
            the <a className="text-[#1565C0] hover:underline" href="https://www.pdp.gov.my" target="_blank" rel="noopener noreferrer">Department of Personal Data Protection Malaysia (JPDP)</a>.
          </p>
        </section>

        <section className="border-t pt-6 mt-10">
          <p className="text-xs text-gray-500">
            This policy is reviewed annually. Substantial changes will be communicated via the website
            and, where applicable, to affected data subjects directly.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            See also: <Link to="/terms" className="text-[#1565C0] hover:underline">Terms & Conditions</Link>
          </p>
        </section>
      </article>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicy;
